import { useMemo } from "react"

import useTracking from "./useTracking"

const useProductTracking = () => {
  const { track, trackRender, trackView, trackClick } = useTracking()

  /**
   * Tracks when a user visits the product or class page. Adds additional product details.
   */
  const trackProductRender = ({ elementRef, pageId, cls, variant }) => {
    const extraProps = {
      pageId,
      brand: cls ? "mbg classes" : "mbg supplements",
      category: cls ? "Classes" : "Supplements",
      ...(variant && {
        product_id: variant.product?.productId,
        variant: variant.variantId,
        price: variant.price,
        name: variant.product?.title,
      }),
      ...(cls && {
        product_id: cls.id,
        price: cls.salePrice || cls.price,
        name: cls.title,
      }),
    }

    trackRender(elementRef, null, extraProps, (props) => track("Product Viewed", props))
  }

  const getClassProps = (cls) => ({
    brand: "mbg classes",
    category: "Classes",
    price: cls.salePrice || cls.price,
    name: cls.title,
  })

  const getProductProps = (variant) => ({
    brand: "mbg supplements",
    category: "Supplements",
    price: variant.price,
    name: variant.product?.title,
  })

  /**
   * Tracks when an element that contains products is viewed.
   */
  const trackProductListView = ({ elementRef, trackingId, items }) => {
    const extraProps = useMemo(() => {
      const props = { list_id: trackingId, products: [] }

      items.forEach((item, idx) => {
        const itemProps = {
          product_id: item.itemId,
          label: "product_id",
          position: idx + 1,
        }

        if (item.itemType === "Class") {
          if (item.cls) {
            Object.assign(itemProps, getClassProps(item.cls))
          }
        } else if (item.itemType === "Product") {
          const variant = item.shopifyProductVariant
          if (variant) {
            Object.assign(itemProps, getProductProps(variant))
          }
        }

        props.products.push(itemProps)
      })

      return props
    }, [trackingId, items])

    trackView(
      elementRef,
      null,
      extraProps,
      (props) => track("Product List Viewed", props),
      "productListViewed",
    )
  }

  /**
   * Tracks when a user clicks on a product element.
   */
  const trackProductClick = ({ elementRef, item }) => {
    const extraProps = useMemo(() => {
      const props = {
        product_id: item.itemId,
        label: "product_id",
      }

      if (item.itemType === "Class") {
        if (item.cls) {
          Object.assign(props, getClassProps(item.cls))
        }
      } else if (item.itemType === "Product") {
        const variant = item.shopifyProductVariant
        if (variant) {
          Object.assign(props, getProductProps(variant))
        }
      }

      return props
    }, [item])

    trackClick(elementRef, null, extraProps, (props) => track("Product Clicked", props))
  }

  return { trackProductRender, trackProductListView, trackProductClick }
}

export default useProductTracking
